import { sum } from 'lodash';

import { BLACK } from '~/core/constants';
import { ColorModel, MaterialModel, MaterialsAPIItem } from '~/core/models';
import { parseHexColor } from '~/redux/slices/color/color.utils';

type PriceStats = {
  minPrice: number | null;
  maxPrice: number | null;
  avgPrice: number | null;
};

const calculatePriceStats = (
  apiMaterial: MaterialsAPIItem,
  apiMaterialsLength: number
): PriceStats => {
  const prices = apiMaterial.colors
    .map((color) => color.price)
    .filter((price) => price !== null);

  return prices.length === 0
    ? { minPrice: null, maxPrice: null, avgPrice: null }
    : {
        minPrice: Math.min(...prices),
        maxPrice: Math.max(...prices),
        avgPrice: sum(prices) / apiMaterialsLength,
      };
};

const getParsedMaterialColors = (colors: ColorModel[]): ColorModel[] =>
  colors.map((color) => {
    const overlayColor = parseHexColor(color.overlayColor, BLACK);
    const cleanedColor: ColorModel = {
      ...color,
      overlayColor,
    };

    return cleanedColor;
  });

/**
 * The `qteGrpID` is selected from the thickness options inside colors array.
 * If available, the preferred `qteGrpID` is the same of the 3CM thickness option.
 * If not available, returns the first thickness of the first color.
 * Worst case scenario, no thickness is found at all, returns 0.
 */
const getQteGrpIDFromColors = (colors: ColorModel[]): number =>
  colors[0]?.thicknessOptions[0]?.qteGrpID || 0;

export const parseApiResponse = (response: MaterialsAPIItem[]) =>
  response.map((apiItem) => {
    const { minPrice, maxPrice, avgPrice } = calculatePriceStats(
      apiItem,
      response.length
    );

    const fixedColors = getParsedMaterialColors(apiItem.colors);
    const qteGrpID = getQteGrpIDFromColors(fixedColors);

    const fixedMaterial: MaterialModel = {
      ...apiItem,
      id: `${qteGrpID}-${apiItem.description}`,
      minPrice,
      maxPrice,
      avgPrice,
      productLine: apiItem.description,
      qteGrpID,
      colors: fixedColors,
    };

    return fixedMaterial;
  });

export const findMaterialByAttributes = ({
  materials,
  description,
}: {
  materials: MaterialModel[];
  description?: string | null;
}): MaterialModel | undefined =>
  materials.length && description
    ? materials.find((material) => material.description === description)
    : undefined;
